import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, Joyride, STATUS } from 'react-joyride';
import type { EventData, Options, Props, Step } from 'react-joyride';

import { useAuth } from 'contexts/AuthContext';
import { useUserTourProgress } from 'hooks/useUserTourProgress';
import { events } from 'services/api';
import { buildAnalyticsEventPayload } from 'utils/analyticsEventContext';
import { setGuidedTourPromptBlocker } from 'utils/foregroundPrompt';

export type UserGuidedTourStepDefinition = {
  selector: string;
  title: React.ReactNode;
  content: React.ReactNode;
  placement?: Step['placement'];
};

export type UserGuidedTourProps = {
  tourKey: string;
  isActive: boolean;
  launchToken: number;
  stepDefinitions: UserGuidedTourStepDefinition[];
  analyticsScreenName: string;
  analyticsSurface: string;
  analyticsMetadata: Record<string, unknown>;
  analyticsPathname?: string;
  defaultPlacement?: Step['placement'];
  autoStartKey?: unknown;
  scrollToFirstStep?: boolean;
  joyrideOptions?: Partial<Options>;
};

type TourScrollSnapshot = {
  documentLeft: number;
  documentTop: number;
  root: HTMLElement | null;
  rootLeft: number;
  rootTop: number;
};

const captureTourScroll = (): TourScrollSnapshot => {
  const root = document.getElementById('root');
  return {
    documentLeft: document.documentElement.scrollLeft || document.body.scrollLeft,
    documentTop: document.documentElement.scrollTop || document.body.scrollTop,
    root,
    rootLeft: root?.scrollLeft ?? 0,
    rootTop: root?.scrollTop ?? 0,
  };
};

const restoreTourScroll = (snapshot: TourScrollSnapshot) => {
  window.requestAnimationFrame(() => {
    document.documentElement.scrollLeft = snapshot.documentLeft;
    document.documentElement.scrollTop = snapshot.documentTop;
    document.body.scrollLeft = snapshot.documentLeft;
    document.body.scrollTop = snapshot.documentTop;
    if (snapshot.root?.isConnected) {
      snapshot.root.scrollLeft = snapshot.rootLeft;
      snapshot.root.scrollTop = snapshot.rootTop;
    }
  });
};

const DEFAULT_JOYRIDE_OPTIONS: Partial<Options> = {
  buttons: ['back', 'close', 'skip', 'primary'],
  overlayClickAction: false,
  scrollDuration: 300,
  showProgress: true,
  skipBeacon: true,
  targetWaitTimeout: 1500,
  zIndex: 650,
};

const DEFAULT_JOYRIDE_STYLES: Props['styles'] = {
  buttonClose: {
    fontFamily: 'Inter, sans-serif',
    padding: '1rem',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  tooltip: {
    borderRadius: 24,
    fontFamily: 'Inter, sans-serif',
    maxWidth: 'calc(100vw - 2rem)',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'optimizeLegibility',
  },
  tooltipContainer: {
    padding: '0.5rem',
    textAlign: 'left' as const,
  },
  tooltipContent: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '1.5rem',
    paddingTop: '0.5rem',
    paddingBottom: '0rem',
  },
  tooltipFooter: {
    fontFamily: 'Inter, sans-serif',
  },
  tooltipTitle: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: 0,
  },
};

const isTargetReady = (selector: string): boolean => {
  const element = document.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const { width, height } = element.getBoundingClientRect();
  if (width === 0 && height === 0) {
    return false;
  }

  const styles = window.getComputedStyle(element);
  return styles.display !== 'none' && styles.visibility !== 'hidden';
};

const waitForTargetReady = (selector: string, timeoutMs = 1500): Promise<void> =>
  new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
      if (isTargetReady(selector) || Date.now() - startedAt >= timeoutMs) {
        resolve();
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });

const getAvailableSteps = (
  stepDefinitions: UserGuidedTourStepDefinition[],
  defaultPlacement: Step['placement'],
): Step[] => {
  const shouldCenterInViewport = window.matchMedia('(max-width: 639px)').matches;
  return stepDefinitions
    .filter(({ selector }) => document.querySelector(selector))
    .map(({ selector, title, content, placement }) => ({
      target: selector,
      title,
      content,
      before: async () => {
        await waitForTargetReady(selector);
      },
      disableBeacon: true,
      placement: shouldCenterInViewport ? 'center' : placement ?? defaultPlacement,
    }));
};

export const UserGuidedTour: React.FC<UserGuidedTourProps> = ({
  tourKey,
  isActive,
  launchToken,
  stepDefinitions,
  analyticsScreenName,
  analyticsSurface,
  analyticsMetadata,
  analyticsPathname,
  defaultPlacement = 'bottom',
  autoStartKey,
  scrollToFirstStep = false,
  joyrideOptions,
}) => {
  const { user } = useAuth();
  const { hasSeen, isLoading, markCompleted, markDismissed } = useUserTourProgress(tourKey);
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [autoStartSettled, setAutoStartSettled] = useState(false);
  const autoStartedRef = useRef(false);
  const outcomeHandledRef = useRef(false);
  const scrollSnapshotRef = useRef<TourScrollSnapshot | null>(null);

  const trackEvent = useCallback(
    (eventType: string, eventData: Record<string, unknown>) => {
      Promise.resolve(
        events.track(
          eventType,
          buildAnalyticsEventPayload(
            {
              ...eventData,
              screen_name: analyticsScreenName,
              surface: analyticsSurface,
            },
            user,
            analyticsPathname ? { pathname: analyticsPathname } : undefined,
          ),
        ),
      ).catch((error) => {
        console.error(`Failed to track ${eventType}`, error);
      });
    },
    [analyticsPathname, analyticsScreenName, analyticsSurface, user],
  );

  const startTour = useCallback(
    (trigger: 'auto' | 'manual') => {
      if (!isActive) {
        return false;
      }

      const nextSteps = getAvailableSteps(stepDefinitions, defaultPlacement);
      if (!nextSteps.length) {
        return false;
      }

      outcomeHandledRef.current = false;
      if (!scrollSnapshotRef.current) {
        scrollSnapshotRef.current = captureTourScroll();
      }
      setSteps(nextSteps);
      setRun(true);

      const payload = {
        tour_key: tourKey,
        ...analyticsMetadata,
        trigger,
        step_count: nextSteps.length,
      };
      trackEvent('tour_started', payload);
      if (trigger === 'manual') {
        trackEvent('tour_relaunched', payload);
      }

      return true;
    },
    [analyticsMetadata, defaultPlacement, isActive, stepDefinitions, tourKey, trackEvent],
  );

  useEffect(() => {
    if (!isActive) {
      setRun(false);
      setSteps([]);
      setAutoStartSettled(false);
      autoStartedRef.current = false;
    }
  }, [isActive]);

  useEffect(() => {
    const blocked = isActive && (run || isLoading || (!hasSeen && !autoStartSettled));
    setGuidedTourPromptBlocker(tourKey, blocked);
    return () => setGuidedTourPromptBlocker(tourKey, false);
  }, [autoStartSettled, hasSeen, isActive, isLoading, run, tourKey]);

  useEffect(() => {
    if (!isActive || isLoading || hasSeen || autoStartedRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const started = startTour('auto');
      setAutoStartSettled(true);
      if (started) {
        autoStartedRef.current = true;
      }
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoStartKey, hasSeen, isActive, isLoading, startTour]);

  useEffect(() => {
    if (!isActive || launchToken === 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      startTour('manual');
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isActive, launchToken, startTour]);

  const handleDismiss = useCallback(() => {
    if (outcomeHandledRef.current) {
      return;
    }

    outcomeHandledRef.current = true;
    setRun(false);
    setAutoStartSettled(true);
    if (scrollSnapshotRef.current) {
      restoreTourScroll(scrollSnapshotRef.current);
      scrollSnapshotRef.current = null;
    }
    void Promise.resolve(markDismissed())
      .then(() => {
        trackEvent('tour_dismissed', {
          tour_key: tourKey,
          ...analyticsMetadata,
        });
      })
      .catch((error) => {
        console.error(`Failed to persist dismissed tour progress for ${tourKey}`, error);
      });
  }, [analyticsMetadata, markDismissed, tourKey, trackEvent]);

  const handleComplete = useCallback(() => {
    if (outcomeHandledRef.current) {
      return;
    }

    outcomeHandledRef.current = true;
    setRun(false);
    setAutoStartSettled(true);
    if (scrollSnapshotRef.current) {
      restoreTourScroll(scrollSnapshotRef.current);
      scrollSnapshotRef.current = null;
    }
    void Promise.resolve(markCompleted())
      .then(() => {
        trackEvent('tour_completed', {
          tour_key: tourKey,
          ...analyticsMetadata,
        });
      })
      .catch((error) => {
        console.error(`Failed to persist completed tour progress for ${tourKey}`, error);
      });
  }, [analyticsMetadata, markCompleted, tourKey, trackEvent]);

  const handleJoyrideCallback = useCallback(
    (data: EventData) => {
      if (data.status === STATUS.FINISHED) {
        handleComplete();
        return;
      }

      if (data.status === STATUS.SKIPPED || data.action === ACTIONS.CLOSE) {
        handleDismiss();
      }
    },
    [handleComplete, handleDismiss],
  );

  const shouldRender = useMemo(() => isActive && steps.length > 0, [isActive, steps.length]);
  const resolvedOptions = useMemo<Partial<Options>>(
    () => ({
      ...DEFAULT_JOYRIDE_OPTIONS,
      ...joyrideOptions,
      overlayClickAction: false,
    }),
    [joyrideOptions],
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <Joyride
      continuous
      locale={{
        last: 'Done',
      }}
      onEvent={handleJoyrideCallback}
      options={resolvedOptions}
      run={run}
      scrollToFirstStep={scrollToFirstStep}
      steps={steps}
      styles={DEFAULT_JOYRIDE_STYLES}
    />
  );
};
